import type { BouncySumsConfig, BouncySumsGameData, BouncySumsLabels } from '@/features/bouncySums/types';

const DEFAULT_TABLES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const DEFAULT_GAME_TIME_SECONDS = 60;
const DEFAULT_BPM = 110;

const toFiniteNumber = (value: unknown, fallback: number) => {
	const numericValue = Number(value);

	return Number.isFinite(numericValue) ? numericValue : fallback;
};

const readStringArray = (value: unknown): string[] | null => {
	if (!Array.isArray(value)) {
		return null;
	}

	const strings = value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);

	return strings.length ? strings : null;
};

const readPlaylistSections = (value: unknown): Partial<Record<number, number>> | null => {
	if (!value || typeof value !== 'object' || Array.isArray(value)) {
		return null;
	}

	const playlistSections = Object.entries(value).reduce<Partial<Record<number, number>>>((sections, [key, id]) => {
		const table = Number(key);
		const playlistSectionId = Number(id);

		if (
			Number.isInteger(table) &&
			table >= 1 &&
			table <= 10 &&
			Number.isInteger(playlistSectionId) &&
			playlistSectionId > 0
		) {
			sections[table] = playlistSectionId;
		}

		return sections;
	}, {});

	return Object.keys(playlistSections).length ? playlistSections : null;
};

const getPlaylistSectionTables = (playlistSections: Partial<Record<number, number>>) => {
	const tables = Object.keys(playlistSections)
		.map((item) => Number(item))
		.filter((item) => Number.isInteger(item) && item >= 1 && item <= 10)
		.sort((a, b) => a - b);

	return tables.length ? tables : null;
};

export const normalizeBouncySumsTables = (tables?: number[] | null) => {
	if (!tables?.length) {
		return DEFAULT_TABLES;
	}

	const normalized = tables.filter((item) => Number.isInteger(item) && item >= 1 && item <= 10);

	return normalized.length ? [...new Set(normalized)] : DEFAULT_TABLES;
};

export const getBouncySumsPlaylistSectionIds = (
	tables: number[],
	playlistSections: Partial<Record<number, number>>,
) => {
	return tables
		.map((table) => playlistSections[table])
		.filter((playlistSectionId): playlistSectionId is number => Number.isInteger(playlistSectionId));
};

export const getBouncySumsConfig = (
	gameData: BouncySumsGameData | null | undefined,
	labels: BouncySumsLabels,
	highScore?: number,
): BouncySumsConfig => {
	const playlistSections = readPlaylistSections(gameData?.playlist_sections) ?? {};
	const tables = getPlaylistSectionTables(playlistSections) ?? DEFAULT_TABLES;
	const motivationalMessages =
		readStringArray(gameData?.motivation_messages) ??
		labels.motivationalMessages.filter((item) => item.trim().length > 0);

	return {
		gameTimeSeconds: Math.max(10, toFiniteNumber(gameData?.game_time, DEFAULT_GAME_TIME_SECONDS)),
		bpm: DEFAULT_BPM,
		tables: normalizeBouncySumsTables(tables),
		playlistSections,
		highScore: Math.max(0, Math.round(toFiniteNumber(highScore ?? gameData?.high_score, 0))),
		speedupMessage: String(gameData?.speedup_message ?? labels.speedupMessage),
		motivationalMessages,
	};
};
